import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { 
  Camera, 
  Shield, 
  Eye, 
  Lock, 
  Users,
  Clock,
  FileText,
  CheckCircle,
  XCircle,
  ArrowRight,
  Play,
  MapPin,
  Heart
} from 'lucide-react';

const Cameras = () => {
  const cameraFeatures = [
    {
      icon: Shield,
      title: "Internal Security Only",
      description: "Cameras are installed in all classrooms for supervision and incident review, not for public viewing."
    },
    {
      icon: Lock,
      title: "Secure Storage",
      description: "Footage is stored securely and may only be reviewed by management, licensing officers, or with parents."
    },
    {
      icon: Users,
      title: "Parent Access",
      description: "Parents may review camera footage with staff following an incident or concern about their child."
    },
    {
      icon: FileText,
      title: "Staff Development",
      description: "Cameras serve as a useful tool for staff development and training purposes when appropriate."
    }
  ];

  const transparencyBenefits = [
    "Additional accountability for all staff members",
    "Quick resolution of parent concerns or questions",
    "Objective documentation of incidents when they occur",
    "Support for staff training and professional development",
    "Enhanced safety monitoring and supervision",
    "Peace of mind for families about their child's care"
  ];

  const privacyProtections = [
    {
      feature: "Live Streaming",
      available: false,
      description: "No live streaming or real-time viewing by parents or public"
    },
    {
      feature: "Public Access",
      available: false,
      description: "Footage is never publicly accessible or shared on social media"
    },
    {
      feature: "External Sharing", 
      available: false,
      description: "Footage is not shared with unauthorized third parties"
    },
    {
      feature: "Authorized Review",
      available: true,
      description: "Only management, licensing, and parents (with staff) can review footage"
    },
    {
      feature: "Incident Documentation",
      available: true,
      description: "Used to document and review incidents for transparency"
    },
    {
      feature: "Staff Training",
      available: true,
      description: "Helps improve care quality through professional development"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-purple-50 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <Badge variant="secondary" className="bg-maple-red text-white mb-6">
              🍁 Camera Policy & Transparency
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-navy-primary mb-6">
              Transparent Security for Peace of Mind
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Maple Kids Academy uses a secure camera system in all classrooms and shared spaces 
              to support supervision, safety, and transparency. Our cameras provide additional 
              accountability while respecting every child's dignity and privacy.
            </p>
          </div>
        </div>
      </section>

      {/* Camera System Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-primary mb-6">
              How Our Camera System Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our security camera system is designed with transparency and child protection as top priorities. 
              Here's exactly how we use cameras to enhance safety and accountability.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {cameraFeatures.map((feature, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-t-4 border-t-blue-primary">
                <CardHeader className="pb-4 text-center">
                  <div className="p-4 bg-blue-50 rounded-full w-fit mx-auto mb-4 group-hover:bg-blue-100 transition-colors">
                    <feature.icon className="h-8 w-8 text-blue-primary" />
                  </div>
                  <CardTitle className="text-lg text-navy-primary">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 text-center">
                  <CardDescription className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Privacy & Access Controls */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-primary mb-6">
              Privacy Protection & Access Controls
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We maintain strict controls over who can access camera footage and how it's used, 
              ensuring your child's privacy is always protected.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* What We DON'T Do */}
              <Card className="border-l-4 border-l-red-500 bg-red-50/50">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2 text-red-700">
                    <XCircle className="h-5 w-5" />
                    <span>What We DON'T Do</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {privacyProtections
                    .filter(item => !item.available)
                    .map((item, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <XCircle className="h-4 w-4 text-red-500 mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-red-700">{item.feature}</p>
                          <p className="text-sm text-red-600">{item.description}</p>
                        </div>
                      </div>
                    ))}
                </CardContent>
              </Card>

              {/* What We DO */}
              <Card className="border-l-4 border-l-green-500 bg-green-50/50">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2 text-green-700">
                    <CheckCircle className="h-5 w-5" />
                    <span>What We DO</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {privacyProtections
                    .filter(item => item.available)
                    .map((item, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-green-700">{item.feature}</p>
                          <p className="text-sm text-green-600">{item.description}</p>
                        </div>
                      </div>
                    ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits for Families */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-navy-primary mb-6">
                Benefits for Families
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                The goal of our camera policy is to provide additional accountability and peace of mind for families. 
                It also serves as a useful tool for staff development and for resolving parent concerns quickly and thoroughly.
              </p>

              <div className="space-y-4 mb-8">
                {transparencyBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-primary mt-1 flex-shrink-0" />
                    <p className="text-gray-700">{benefit}</p>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 p-6 rounded-xl">
                <h4 className="font-semibold text-navy-primary mb-3">Parent Access Process</h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  If you have concerns about an incident involving your child, you can request to review 
                  relevant camera footage with a staff member present. We'll schedule this review promptly 
                  and walk through any questions you may have about your child's care.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl p-8 text-center">
                <Camera className="h-20 w-20 text-blue-primary mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-navy-primary mb-4">
                  Always Professional, Never Invasive
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Our cameras are positioned thoughtfully to monitor classrooms and common areas 
                  while respecting privacy during personal care routines. 
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-white/60 rounded-lg p-3">
                    <Eye className="h-6 w-6 text-blue-primary mx-auto mb-2" />
                    <p className="font-medium">Visual Monitoring</p>
                    <p className="text-gray-600">Clear view of play areas</p>
                  </div>
                  <div className="bg-white/60 rounded-lg p-3">
                    <Shield className="h-6 w-6 text-green-500 mx-auto mb-2" />
                    <p className="font-medium">Privacy Respected</p>
                    <p className="text-gray-600">Careful positioning</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Policy Details */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-navy-primary mb-6">
                Complete Camera Policy
              </h2>
              <p className="text-xl text-gray-600">
                Our full camera policy ensures transparency while maintaining the highest standards 
                of child protection and privacy.
              </p>
            </div>

            <Card className="shadow-xl bg-white">
              <CardHeader className="text-center pb-6">
                <CardTitle className="text-2xl text-navy-primary flex items-center justify-center space-x-3">
                  <FileText className="h-6 w-6" />
                  <span>Camera Policy Summary</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-navy-primary mb-3">Camera Locations</h4>
                    <ul className="text-gray-600 space-y-2 text-sm">
                      <li>• All classrooms and play areas</li>
                      <li>• Common areas and hallways</li>
                      <li>• Outdoor play spaces</li>
                      <li>• Entry and exit points</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-navy-primary mb-3">Authorized Viewers</h4>
                    <ul className="text-gray-600 space-y-2 text-sm">
                      <li>• Center management and directors</li>
                      <li>• Fraser Health licensing officers</li>
                      <li>• Parents (with staff present)</li>
                      <li>• Law enforcement (with warrant)</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-navy-primary mb-3">Storage & Retention</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Camera footage is stored securely on-site with encrypted storage systems. 
                    Footage is retained for 30 days unless needed for incident investigation or 
                    licensing review. All footage is automatically deleted after the retention period.
                  </p>
                </div>

                <div className="bg-blue-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-navy-primary mb-3">Your Rights as Parents</h4>
                  <ul className="text-gray-600 space-y-2 text-sm">
                    <li>• Request to review footage involving your child</li>
                    <li>• Receive explanation of camera policies during enrollment</li>
                    <li>• Ask questions about camera placement and usage</li>
                    <li>• Understand how footage may be used in incident reports</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-purple-600 to-pink-500 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">
                Come Visit Our Happy Place!
              </h2>
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3 bg-white/20 p-4 rounded-xl">
                  <MapPin className="h-5 w-5 mt-1 flex-shrink-0 text-yellow-300" />
                  <div>
                    <p className="font-bold text-lg">10681 248 St, Maple Ridge, BC</p>
                    <p className="text-purple-100">Easy parking for families</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 bg-white/20 p-4 rounded-xl">
                  <Clock className="h-5 w-5 mt-1 flex-shrink-0 text-green-300" />
                  <div>
                    <p className="font-bold text-lg">Monday - Friday: 7:00 AM - 5:00 PM</p>
                    <p className="text-purple-100">Closed weekends and holidays</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-yellow-400 hover:bg-yellow-500 text-purple-800 font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  <Link to="/contact">Book Your Visit</Link>
                </Button>
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-white/20 hover:bg-white/30 text-white border-2 border-white hover:border-yellow-300 font-bold px-8 py-4 rounded-full transition-all"
                >
                  <Link to="/enroll">Join Our Family</Link>
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white/20 backdrop-blur-sm p-8 rounded-2xl border-2 border-white/30">
                <h3 className="text-2xl font-bold mb-6 text-center">
                  Why Choose Maple Kids?
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                      <Users className="h-4 w-4 text-purple-800" />
                    </div>
                    <span className="font-medium">Small groups, big love</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-400 rounded-full flex items-center justify-center">
                      <Shield className="h-4 w-4 text-purple-800" />
                    </div>
                    <span className="font-medium">Licensed & insured</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-pink-400 rounded-full flex items-center justify-center">
                      <Heart className="h-4 w-4 text-purple-800" />
                    </div>
                    <span className="font-medium">Every child is welcome</span>
                  </div>
                </div>
                
                <div className="mt-6 text-center bg-white/20 p-4 rounded-xl">
                  <p className="text-sm font-medium">Ready to join our family?</p>
                  <p className="text-xs text-purple-100 mt-1">Tours available by appointment</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cameras;